/* eslint-disable no-console */
// src/services/apiClient.ts
import axios, { AxiosInstance, AxiosResponse } from 'axios';

import { VITE_API_BASE_URL } from '@/constants/environment';
import { applyTourAdapter, shouldUseTourData } from '@/tours';
import { handleApiError } from '@/utils/error-handler';
import { getFingerprintData } from '@/utils/finger-print';
import { showErrorToast } from '@/utils/snackbar-utils';

const apiClient: AxiosInstance = axios.create({
  baseURL: VITE_API_BASE_URL,
  timeout: 30000, // 30 seconds timeout for all requests
  withCredentials: true, // Ensures cookies are sent with all requests
  headers: {
    ['Content-Type']: 'application/json',
  },
});

let activeTenantId: string | null = null;

export const setTenantId = (id: string | number | null) => {
  activeTenantId = id ? String(id) : null;
};

/**
 * The tenant the interceptor is stamping on requests. Exposed for the handful
 * of calls that cannot go through this instance — the SSE stream is read with
 * `fetch`, and still has to send the same `X-TenantId`.
 */
export const getTenantId = (): string | null => activeTenantId;

export const getCookie = (name: string): string | undefined => {
  const cookieStr = document.cookie;
  const cookies = cookieStr.split('; ').reduce(
    (acc, current) => {
      const [key, value] = current.split('=');
      acc[key] = value;
      return acc;
    },
    {} as Record<string, string>
  );
  return cookies[name];
};

export function deleteCookie(name: string) {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
}

// Delete all cookies for the current domain/path
export function deleteAllCookies() {
  const cookies = document.cookie ? document.cookie.split(';') : [];
  cookies.forEach(cookie => {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
    // Expire for the current path
    document.cookie = `${name}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
    // Also try expiring for root and without path just in case
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
    document.cookie = `${name}=;path=/;domain=${globalThis.location.hostname};expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
  });
}

// 🔹 Request Interceptor
apiClient.interceptors.request.use(async config => {
  // 0. Help & Tours: while a tour is running the request is answered from the
  //    sample-data layer instead of the network. Swapping the adapter here
  //    means axios never opens a connection, so a tour makes no requests for
  //    the data it shows. Session calls (auth, profile, permissions) are not
  //    diverted — see shouldUseTourData. Nothing below this point applies to a
  //    tour request: the fingerprint lookup is itself a network call, and the
  //    CSRF/tenant headers have nothing to authenticate against.
  if (shouldUseTourData(config.url)) {
    return applyTourAdapter(config);
  }

  // 1. Add Device Fingerprint
  try {
    const fingerprintData = await getFingerprintData();
    config.headers['X-Fingerprint'] = JSON.stringify(fingerprintData);
  } catch (error) {
    console.warn('Failed to attach fingerprint:', error);
  }
  const tenantId = activeTenantId;

  if (tenantId) {
    config.headers['X-TenantId'] = String(tenantId);
  }

  // 2. Add CSRF Token (Session ID) for mutating requests
  const sessionId = getCookie(`sv_xid_${activeTenantId}`);
  const mutatingMethods = new Set(['post', 'put', 'delete', 'patch']);

  if (
    sessionId &&
    config.method &&
    mutatingMethods.has(config.method.toLowerCase())
  ) {
    config.headers['X-Sv_xid'] = sessionId;
  } else if (
    !sessionId &&
    config.method &&
    mutatingMethods.has(config.method.toLowerCase())
  ) {
    console.warn(
      `CSRF token not found for ${config.method.toUpperCase()} request to ${config.url}`
    );
  }

  return config;
});

// 🔹 Token Refresh Queue Management
let isRefreshing = false;
let inFlightRefresh: Promise<void> | null = null;

export const triggerTokenRefresh = async () => {
  if (inFlightRefresh) {
    return inFlightRefresh; // Return existing promise so all callers await the same refresh
  }

  isRefreshing = true;
  inFlightRefresh = (async () => {
    try {
      const { refreshToken } = await import('./auth-service');
      await refreshToken();
      processQueue(null);
    } catch (error) {
      processQueue(error);
      throw error; // Re-throw so callers know the refresh actually failed
    } finally {
      isRefreshing = false;
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
};

let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

// Response Interceptor with Token Refresh
let isLoggingOut = false;

function dispatchLogout(reason: string) {
  if (!isLoggingOut) {
    isLoggingOut = true;
    globalThis.dispatchEvent(
      new CustomEvent('auth-logout', {
        detail: { reason },
      })
    );
    // AuthContext will handle the toast and redirect
    isLoggingOut = false;
  }
}

function handleRefreshError(refreshError: any) {
  // Log the error for debugging (avoid logging full Axios error with headers/config)
  console.error('[API] Token refresh failed in interceptor:', {
    name: refreshError?.name,
    message: refreshError?.message,
    code: refreshError?.code,
    status: refreshError?.response?.status,
  });

  const status = refreshError.response?.status;
  const isAuthFailure = status === 401 || status === 403;
  const isNetworkError = !refreshError.response;

  if (isAuthFailure) {
    // Hard auth failure - logout immediately
    console.error('[API] Auth failure detected. Logging out.');
    dispatchLogout('session_expired');
  } else if (isNetworkError) {
    // Network error - let the AuthContext retry logic handle it
    console.warn(
      '[API] Network error during token refresh. AuthContext will retry.'
    );
    // Don't logout here - let the retry mechanism in AuthContext handle it
  } else {
    // Other error - logout with message
    console.error('[API] Unknown error during token refresh. Logging out.');
    dispatchLogout('token_refresh_failed');
  }
}

async function handle401Retry(originalRequest: any) {
  if (isRefreshing) {
    // If already refreshing, queue this request
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then(() => apiClient(originalRequest));
  }

  originalRequest._retry = true;

  try {
    // Use triggerTokenRefresh to ensure single in-flight refresh across the module
    await triggerTokenRefresh();

    // Retry the original request
    return await apiClient(originalRequest);
  } catch (refreshError: any) {
    handleRefreshError(refreshError);
    throw refreshError;
  }
}

function extractValidationMessage(errors: unknown): string {
  // ASP.NET ProblemDetails validation errors carry the real message per
  // field in `errors` (e.g. { SearchEmployee: ["...invalid characters."] })
  // and only a generic sentence in `title`.
  if (errors && typeof errors === 'object') {
    return Object.values(errors)
      .flat()
      .filter((msg): msg is string => typeof msg === 'string')
      .join(' ');
  }
  return '';
}

function formatErrorMessage(error: any): string {
  const responseData = error.response?.data;
  const errorCode = responseData?.errorCode || responseData?.code;
  const validationMessage = extractValidationMessage(responseData?.errors);
  const backendMessage =
    responseData?.message || validationMessage || responseData?.title;

  const errorData = {
    code: errorCode,
    message: backendMessage || error.message,
    status: error.response?.status,
  };

  const userFriendlyMessage = handleApiError(errorData);
  const baseMessage = backendMessage || userFriendlyMessage;
  const finalMessage = errorCode ? `${errorCode}: ${baseMessage}` : baseMessage;

  // Mutate the original error message so that callers catching the error get the formatted message
  error.message = finalMessage;
  return finalMessage;
}

function shouldShowErrorToast(
  status: number | undefined,
  url: unknown,
  skipToast?: boolean
): boolean {
  if (skipToast || status === 401 || status === 403) {
    return false;
  }

  const isTenantInfoNotFound =
    status === 404 &&
    typeof url === 'string' &&
    url.includes('/auth/tenant-info');

  return !isTenantInfoNotFound;
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async error => {
    const originalRequest = error.config;
    const status = error.response?.status;

    // Special case: If get-profile returns 404, skip refresh and logout directly
    if (
      status === 404 &&
      originalRequest?.url?.split(/[?#]/)[0].endsWith('/admin/profile')
    ) {
      deleteAllCookies();
      localStorage.clear();
      sessionStorage.clear();
      throw error;
    }

    // Handle 401 Unauthorized with token refresh
    // Skip retry logic for refresh endpoint itself and already retried requests
    if (
      status === 401 &&
      !originalRequest?._retry &&
      !originalRequest?.skipAuthRetry
    ) {
      return handle401Retry(originalRequest);
    }

    // Handle other errors (non-401 or after retry)
    const finalMessage = formatErrorMessage(error);

    // Show error toast unless it's a 401 (handled silently during refresh),
    // a 403, a 404 from /auth/tenant-info (for which we suppress toasts),
    // or the request explicitly skips the error toast.
    if (
      shouldShowErrorToast(
        status,
        originalRequest?.url,
        originalRequest?.skipErrorToast
      )
    ) {
      showErrorToast(finalMessage);
    }

    const finalError = {
      ...error,
      message: finalMessage,
      userMessage: finalMessage,
    };

    throw finalError;
  }
);

export default apiClient;
